import {
  decodeLbcd,
  encodeLbcd,
  type RadioProgress,
  type WebChannel,
} from './baofeng-web.ts'
import type { RadioTransport } from './web-serial.ts'

const ACK = 0x06
const MEMORY_SIZE = 0x4000
const INDEX_START = 0x00e0
const CHANNEL_START = 0x03e0
const CHANNEL_SIZE = 32
const MAX_CHANNELS = 128
const UPLOAD_RANGES = [
  [0x0020, 0x1740],
  [0x17e0, 0x3120],
] as const

type TKx160Spec = {
  id: string
  model: string
  summary: string
  modelPrefix: readonly number[]
  channelCount: number
  powerLevels: readonly ['Low', 'High']
}

export type TKx160Profile = {
  protocol: 'kenwood-tkx160'
  id: string
  vendor: 'Kenwood'
  model: string
  summary: string
  tags: readonly string[]
  baudRate: 9600
  stopBits: 2
  channelMinimum: 1
  channelMaximum: number
  nameLength: 8
  memorySize: number
  modelPrefix: readonly number[]
  powerLevels: readonly ['Low', 'High']
}

const SPECS: readonly TKx160Spec[] = [
  {
    id: 'kenwood-tk-7160m',
    model: 'TK-7160M',
    summary: 'Kenwood TK-7160M VHF mobile with zone-indexed memories.',
    modelPrefix: [0x4d, 0x36, 0x36, 0x2d, 0x20, 0x04],
    channelCount: MAX_CHANNELS,
    powerLevels: ['Low', 'High'],
  },
  {
    id: 'kenwood-tk-7160k',
    model: 'TK-7160K',
    summary: 'Kenwood TK-7160K VHF mobile with zone-indexed memories.',
    modelPrefix: [0x4d, 0x36, 0x36, 0x2d, 0x20, 0x02],
    channelCount: MAX_CHANNELS,
    powerLevels: ['Low', 'High'],
  },
  {
    id: 'kenwood-tk-8160k',
    model: 'TK-8160K',
    summary: 'Kenwood TK-8160K UHF mobile with zone-indexed memories.',
    modelPrefix: [0x4d, 0x36, 0x37, 0x2d, 0x20, 0x22],
    channelCount: MAX_CHANNELS,
    powerLevels: ['Low', 'High'],
  },
  {
    id: 'kenwood-tk-8160m',
    model: 'TK-8160M',
    summary: 'Kenwood TK-8160M UHF mobile with zone-indexed memories.',
    modelPrefix: [0x4d, 0x36, 0x37, 0x2d, 0x20, 0x44],
    channelCount: MAX_CHANNELS,
    powerLevels: ['Low', 'High'],
  },
  {
    id: 'kenwood-tk-7162',
    model: 'TK-7162',
    summary: 'Kenwood TK-7162 16-channel VHF mobile.',
    modelPrefix: [0x4d, 0x37, 0x31, 0x36, 0x32, 0x00],
    channelCount: 16,
    powerLevels: ['Low', 'High'],
  },
]

export const TKX160_PROFILES: readonly TKx160Profile[] = SPECS.map((spec) => ({
  protocol: 'kenwood-tkx160',
  id: spec.id,
  vendor: 'Kenwood',
  model: spec.model,
  summary: spec.summary,
  tags: ['Experimental', 'Read + write', `${spec.channelCount} channels`, '8-character names'],
  baudRate: 9600,
  stopBits: 2,
  channelMinimum: 1,
  channelMaximum: spec.channelCount,
  nameLength: 8,
  memorySize: MEMORY_SIZE,
  modelPrefix: spec.modelPrefix,
  powerLevels: spec.powerLevels,
}))

function emptyChannel(number: number): WebChannel {
  return {
    number,
    empty: true,
    name: '',
    rxFrequency: null,
    txFrequency: null,
    toneMode: 'none',
    ctcss: null,
    dtcs: null,
    dtcsPolarity: 'N',
    mode: 'FM',
    power: 'Low',
    skip: false,
  }
}

function readUint16LE(raw: Uint8Array, offset: number): number {
  return raw[offset] | (raw[offset + 1] << 8)
}

function writeUint16LE(raw: Uint8Array, offset: number, value: number): void {
  raw[offset] = value & 0xff
  raw[offset + 1] = (value >> 8) & 0xff
}

type DecodedTone = {
  mode: 'none' | 'ctcss' | 'dtcs'
  ctcss: number | null
  dtcs: number | null
  polarity: 'N' | 'R'
}

function decodeTone(value: number): DecodedTone {
  if (value === 0xffff) {
    return { mode: 'none', ctcss: null, dtcs: null, polarity: 'N' }
  }
  if (value & 0x2000) {
    return {
      mode: 'dtcs',
      ctcss: null,
      dtcs: Number((value & 0x01ff).toString(8)),
      polarity: value & 0x8000 ? 'R' : 'N',
    }
  }
  return {
    mode: 'ctcss',
    ctcss: value / 10,
    dtcs: null,
    polarity: 'N',
  }
}

function encodeTone(channel: WebChannel): number {
  if (channel.toneMode === 'ctcss' && channel.ctcss) {
    return Math.round(channel.ctcss * 10)
  }
  if (channel.toneMode === 'dtcs' && channel.dtcs) {
    let value = (Number.parseInt(channel.dtcs.toString(), 8) & 0x01ff) | 0x2800
    if (channel.dtcsPolarity === 'R') value |= 0x8000
    return value
  }
  return 0xffff
}

function indexEntry(
  image: Uint8Array,
  index: number,
): { zone: number; memory: number; slot: number } {
  const offset = INDEX_START + index * 4
  return {
    zone: image[offset],
    memory: image[offset + 1],
    slot: image[offset + 3],
  }
}

function slotForNumber(image: Uint8Array, number: number): number | null {
  const entry = indexEntry(image, number - 1)
  if (entry.zone === 0xff || entry.memory === 0xff || entry.slot === 0xff) {
    return null
  }
  return entry.slot
}

function decodeName(raw: Uint8Array): string {
  return String.fromCharCode(
    ...raw.map((value) => (value === 0xff || value === 0 ? 0x20 : value)),
  ).trim()
}

export function parseTKx160Channel(
  image: Uint8Array,
  profile: TKx160Profile,
  number: number,
): WebChannel {
  const slot = slotForNumber(image, number)
  if (slot === null) return emptyChannel(number)
  const start = CHANNEL_START + slot * CHANNEL_SIZE
  const raw = image.slice(start, start + CHANNEL_SIZE)
  if (raw.byteLength !== CHANNEL_SIZE || raw[0] === 0xff) {
    return emptyChannel(number)
  }
  const rxFrequency = decodeLbcd(raw.slice(2, 6))
  if (!rxFrequency) return emptyChannel(number)
  const txAll = raw.slice(6, 10).every((value) => value === 0xff)
  const txFrequency = txAll ? null : decodeLbcd(raw.slice(6, 10)) ?? rxFrequency
  const txTone = decodeTone(readUint16LE(raw, 14))
  const rxTone = decodeTone(readUint16LE(raw, 12))
  const selected = txTone.mode === 'none' ? rxTone : txTone
  return {
    number,
    empty: false,
    name: decodeName(raw.slice(19, 27)),
    rxFrequency,
    txFrequency,
    toneMode: selected.mode,
    ctcss: selected.ctcss,
    dtcs: selected.dtcs,
    dtcsPolarity: selected.polarity,
    mode: raw[16] & 0x01 ? 'FM' : 'NFM',
    power: raw[17] & 0x20 ? 'High' : 'Low',
    skip: (raw[17] & 0x10) === 0,
  }
}

export function parseTKx160Channels(
  image: Uint8Array,
  profile: TKx160Profile,
): WebChannel[] {
  return Array.from({ length: profile.channelMaximum }, (_, index) =>
    parseTKx160Channel(image, profile, index + 1),
  )
}

function recalculateIndex(image: Uint8Array): void {
  const zoneCounts = Array.from({ length: MAX_CHANNELS }, () => 0)
  const entryFlags = new Uint8Array(16)
  const zoneFlags = new Uint8Array(16)
  for (let index = 0; index < MAX_CHANNELS; index += 1) {
    const entry = indexEntry(image, index)
    if (entry.memory !== 0xff) {
      entryFlags[Math.floor(index / 8)] |= 1 << (index % 8)
      if (entry.zone >= 1 && entry.zone <= MAX_CHANNELS) {
        zoneCounts[entry.zone - 1] += 1
      }
    }
  }
  image.set(entryFlags, 0x0030)
  image.set(entryFlags, 0x0050)
  for (let index = 0; index < MAX_CHANNELS; index += 1) {
    const info = 0x02e0 + index * 2
    const count = zoneCounts[index]
    image[info] = count
      ? (image[info] & 0xfe) | 1
      : image[info] & 0xfe
    image[info + 1] = count
    if (count) zoneFlags[Math.floor(index / 8)] |= 1 << (index % 8)
  }
  image.set(zoneFlags, 0x0020)
}

export function applyTKx160Channel(
  image: Uint8Array,
  profile: TKx160Profile,
  channel: WebChannel,
): void {
  let slot = slotForNumber(image, channel.number)
  if (channel.empty) {
    if (slot !== null) {
      image.fill(
        0xff,
        CHANNEL_START + slot * CHANNEL_SIZE,
        CHANNEL_START + (slot + 1) * CHANNEL_SIZE,
      )
      image.fill(
        0xff,
        INDEX_START + (channel.number - 1) * 4,
        INDEX_START + (channel.number - 1) * 4 + 4,
      )
      recalculateIndex(image)
    }
    return
  }
  if (slot === null) {
    let entryIndex = -1
    for (let index = 0; index < profile.channelMaximum; index += 1) {
      if (indexEntry(image, index).zone === 0xff) {
        entryIndex = index
        break
      }
    }
    for (let candidate = 0; candidate < MAX_CHANNELS; candidate += 1) {
      if (image[CHANNEL_START + candidate * CHANNEL_SIZE] === 0xff) {
        slot = candidate
        break
      }
    }
    if (entryIndex < 0 || slot === null) {
      throw new Error('The radio has no free channel index or slot.')
    }
    image.set(
      [1, channel.number, 0, slot],
      INDEX_START + entryIndex * 4,
    )
  }
  const start = CHANNEL_START + slot * CHANNEL_SIZE
  const raw = image.subarray(start, start + CHANNEL_SIZE)
  if (raw[0] === 0xff) raw.fill(0xff)
  raw[0] = channel.number
  raw[1] = raw[1] === 0xff ? 1 : raw[1]
  if (!channel.rxFrequency) throw new Error('A receive frequency is required.')
  encodeLbcd(channel.rxFrequency, raw.subarray(2, 6))
  if (channel.txFrequency) {
    encodeLbcd(channel.txFrequency, raw.subarray(6, 10))
  } else {
    raw.subarray(6, 10).fill(0xff)
  }
  const tone = encodeTone(channel)
  writeUint16LE(raw, 12, tone)
  writeUint16LE(raw, 14, tone)
  raw[16] =
    (raw[16] & 0xfe) |
    (channel.mode === 'FM' ? 1 : 0)
  raw[17] =
    (raw[17] & 0xcf) |
    (channel.power === 'High' ? 0x20 : 0) |
    (channel.skip ? 0 : 0x10)
  raw[10] = 0x02
  raw[11] = 0x02
  raw[27] &= 0x7f
  raw[28] &= 0x7f
  raw[29] = 0x1f
  const name = channel.name
    .replace(/[^\x20-\x7e]/g, '')
    .slice(0, 8)
    .padEnd(8, ' ')
  for (let index = 0; index < 8; index += 1) {
    raw[19 + index] = name.charCodeAt(index)
  }
  recalculateIndex(image)
}

async function readAck(transport: RadioTransport): Promise<void> {
  if ((await transport.readExact(1, 2500))[0] !== ACK) {
    throw new Error('The Kenwood radio rejected the operation.')
  }
}

async function enter(
  profile: TKx160Profile,
  transport: RadioTransport,
): Promise<void> {
  await transport.discardInput()
  await transport.write(new TextEncoder().encode('PROGRAM'))
  if ((await transport.readExact(1, 2500))[0] !== 0x16) {
    throw new Error('The radio refused high-speed programming mode.')
  }
  await transport.reconfigure?.({ baudRate: 19200 })
  await readAck(transport)
  await transport.write(Uint8Array.of(0x02))
  const identity = await transport.readExact(8, 2500)
  if (!profile.modelPrefix.every((value, index) => identity[index] === value)) {
    throw new Error(`The connected radio does not identify as ${profile.model}.`)
  }
  await transport.write(Uint8Array.of(ACK))
  await readAck(transport)
}

export async function readTKx160RadioWithTransport(
  profile: TKx160Profile,
  transport: RadioTransport,
  onProgress?: (progress: RadioProgress) => void,
): Promise<{ image: Uint8Array; reportedModel: string }> {
  await enter(profile, transport)
  try {
    await transport.write(new TextEncoder().encode('hRDA'))
    await readAck(transport)
    const image = new Uint8Array(MEMORY_SIZE).fill(0xff)
    let highest = 0
    while (true) {
      const command = (await transport.readExact(1, 4000))[0]
      if (command === 0x45) break
      if (command !== 0x5a && command !== 0x57) {
        throw new Error(`Unexpected TK-x160 clone command 0x${command.toString(16)}.`)
      }
      const header = await transport.readExact(3, 2500)
      const address = (header[0] << 8) | header[1]
      const length = header[2]
      const data =
        command === 0x5a
          ? new Uint8Array(length).fill(0xff)
          : await transport.readExact(length, 4000)
      image.set(data, address)
      highest = Math.max(highest, address + length)
      await transport.write(Uint8Array.of(ACK))
      onProgress?.({
        current: highest,
        total: MEMORY_SIZE,
        label: 'Reading Kenwood TK-x160 memory',
      })
    }
    return { image, reportedModel: profile.model }
  } finally {
    await transport.write(new TextEncoder().encode('E'))
    await transport.readExact(1, 2000).catch(() => new Uint8Array(0))
  }
}

export async function writeTKx160RadioWithTransport(
  profile: TKx160Profile,
  image: Uint8Array,
  transport: RadioTransport,
  onProgress?: (progress: RadioProgress) => void,
): Promise<void> {
  if (image.byteLength < MEMORY_SIZE) {
    throw new Error('The selected image is smaller than TK-x160 memory.')
  }
  await enter(profile, transport)
  try {
    const addresses: number[] = []
    for (const [start, end] of UPLOAD_RANGES) {
      for (let address = start; address < end; address += 0x40) {
        addresses.push(address)
      }
    }
    for (let index = 0; index < addresses.length; index += 1) {
      const address = addresses[index]
      const length = Math.min(0x40, MEMORY_SIZE - address)
      const packet = new Uint8Array(4 + length)
      packet.set([
        0x57,
        (address >>> 8) & 0xff,
        address & 0xff,
        length,
      ])
      packet.set(image.slice(address, address + length), 4)
      await transport.write(packet)
      await readAck(transport)
      onProgress?.({
        current: index + 1,
        total: addresses.length,
        label: 'Writing Kenwood TK-x160 memory',
      })
    }
  } finally {
    await transport.write(new TextEncoder().encode('E'))
    await transport.readExact(1, 2000).catch(() => new Uint8Array(0))
  }
}
